import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { SAVED_FILTERS } from "../ticker/TickerSavedBar";
import SavedPagerPage from "./SavedPagerPage";
import "./MostVisitedPager.css";

/*
|-----------------------------------------------------------------------------
| Android Saved Pager — Transform-Based Horizontal Paging
|-----------------------------------------------------------------------------
|
| Chrome Android can lose the horizontal gesture when an overflow-x pager
| contains pages that also own overflow-y scrolling. This is the same nested
| scroll conflict that previously affected Focus Mode.
|
| Android therefore uses a translated track instead of native horizontal
| scrolling. Every saved page remains the sole native vertical scroll owner.
| Touch Events only claim the gesture after a clear horizontal axis lock;
| vertical movement is abandoned immediately and remains fully native.
|
*/

const FILTERS = SAVED_FILTERS.map(({ id }) => id);
const VISUAL_FILTERS = [...FILTERS].reverse();

const AXIS_LOCK_DISTANCE = 10;
const HORIZONTAL_AXIS_RATIO = 1.3;
const SWIPE_MIN_DISTANCE = 50;
const SWIPE_DISTANCE_RATIO = 0.14;
const SWIPE_MIN_VELOCITY = 0.4;
const SWIPE_MIN_VELOCITY_DISTANCE = 16;
const TRACK_TRANSITION_MS = 240;
const CLICK_SUPPRESSION_MS = 320;
const EDGE_DRAG_RESISTANCE = 0.3;

const clamp = (value, minimum, maximum) =>
  Math.min(maximum, Math.max(minimum, value));

const getSafeFilter = (filter) =>
  FILTERS.includes(filter) ? filter : FILTERS[0];

const getFilterIndex = (filter) =>
  FILTERS.indexOf(getSafeFilter(filter));

const getVisualIndex = (filter) =>
  VISUAL_FILTERS.indexOf(getSafeFilter(filter));

const dispatchSavedPagerProgress = (progress) => {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent("savedPagerProgress", {
      detail: { progress },
    })
  );
};

const SavedPagerAndroid = ({
  pages = {},
  favicons = {},
  onMarkAsRead,
  onNewsOpen,
  activeFilter = FILTERS[0],
  onFilterChange,
  scrollToTopSignal,
  onActivePageAtTopChange,
  savedLinks,
  onToggleSaved,
  onShare,
  onView,
}) => {
  const containerRef = useRef(null);
  const pageRefs = useRef({});
  const gestureRef = useRef(null);
  const settleTimerRef = useRef(null);
  const progressRafRef = useRef(null);
  const pendingProgressRef = useRef(0);
  const pendingVisualIndexRef = useRef(null);
  const internalPageChangeRef = useRef(false);
  const suppressClickUntilRef = useRef(0);

  const initialActiveIndex = getFilterIndex(activeFilter);
  const initialVisualIndex = getVisualIndex(activeFilter);

  const [activeIndex, setActiveIndex] = useState(initialActiveIndex);
  const [visualIndex, setVisualIndex] = useState(initialVisualIndex);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === "undefined" ? 0 : window.innerWidth || 0
  );
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const activeIndexRef = useRef(activeIndex);
  const visualIndexRef = useRef(visualIndex);
  const isAnimatingRef = useRef(isAnimating);

  activeIndexRef.current = activeIndex;
  visualIndexRef.current = visualIndex;
  isAnimatingRef.current = isAnimating;

  const clearSettleTimer = useCallback(() => {
    if (
      settleTimerRef.current !== null &&
      typeof window !== "undefined"
    ) {
      window.clearTimeout(settleTimerRef.current);
    }

    settleTimerRef.current = null;
  }, []);

  const cancelProgressFrame = useCallback(() => {
    if (
      progressRafRef.current !== null &&
      typeof cancelAnimationFrame === "function"
    ) {
      cancelAnimationFrame(progressRafRef.current);
    }

    progressRafRef.current = null;
  }, []);

  const publishProgress = useCallback((progress) => {
    pendingProgressRef.current = clamp(
      progress,
      0,
      VISUAL_FILTERS.length - 1
    );

    if (progressRafRef.current !== null) return;

    if (typeof requestAnimationFrame !== "function") {
      dispatchSavedPagerProgress(pendingProgressRef.current);
      return;
    }

    progressRafRef.current = requestAnimationFrame(() => {
      progressRafRef.current = null;
      dispatchSavedPagerProgress(pendingProgressRef.current);
    });
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const updateWidth = () => {
      const rect = container.getBoundingClientRect();

      if (rect.width > 0) {
        setViewportWidth(rect.width);
      }
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  const commitVisualIndex = useCallback(
    (targetVisualIndex) => {
      const safeVisualIndex = clamp(
        targetVisualIndex,
        0,
        VISUAL_FILTERS.length - 1
      );
      const nextFilter = VISUAL_FILTERS[safeVisualIndex];
      const nextActiveIndex = getFilterIndex(nextFilter);

      pendingVisualIndexRef.current = null;
      visualIndexRef.current = safeVisualIndex;
      isAnimatingRef.current = false;

      setVisualIndex(safeVisualIndex);
      setDragOffset(0);
      setIsDragging(false);
      setIsAnimating(false);
      publishProgress(safeVisualIndex);

      if (nextActiveIndex === activeIndexRef.current) {
        return;
      }

      activeIndexRef.current = nextActiveIndex;
      setActiveIndex(nextActiveIndex);

      const page = pageRefs.current[nextFilter];
      const pageScrollTop = page?.scrollTop || 0;

      onActivePageAtTopChange?.(pageScrollTop === 0);

      internalPageChangeRef.current = true;
      onFilterChange?.(nextFilter);
    },
    [onActivePageAtTopChange, onFilterChange, publishProgress]
  );

  const finishTrackTransition = useCallback(() => {
    clearSettleTimer();

    const pendingVisualIndex = pendingVisualIndexRef.current;

    if (pendingVisualIndex === null) {
      isAnimatingRef.current = false;
      setIsAnimating(false);
      setIsDragging(false);
      setDragOffset(0);
      publishProgress(visualIndexRef.current);
      return;
    }

    commitVisualIndex(pendingVisualIndex);
  }, [clearSettleTimer, commitVisualIndex, publishProgress]);

  const beginTrackTransition = useCallback(
    (targetVisualIndex) => {
      clearSettleTimer();

      const safeVisualIndex = clamp(
        targetVisualIndex ?? visualIndexRef.current,
        0,
        VISUAL_FILTERS.length - 1
      );

      pendingVisualIndexRef.current = safeVisualIndex;
      visualIndexRef.current = safeVisualIndex;
      isAnimatingRef.current = true;

      setIsDragging(false);
      setIsAnimating(true);
      setDragOffset(0);
      setVisualIndex(safeVisualIndex);
      publishProgress(safeVisualIndex);

      if (typeof window !== "undefined") {
        settleTimerRef.current = window.setTimeout(
          finishTrackTransition,
          TRACK_TRANSITION_MS + 80
        );
      }
    },
    [clearSettleTimer, finishTrackTransition, publishProgress]
  );

  useLayoutEffect(() => {
    const nextActiveIndex = getFilterIndex(activeFilter);
    const nextVisualIndex = getVisualIndex(activeFilter);

    if (
      internalPageChangeRef.current &&
      nextActiveIndex === activeIndexRef.current
    ) {
      internalPageChangeRef.current = false;
      return;
    }

    clearSettleTimer();

    gestureRef.current = null;
    pendingVisualIndexRef.current = null;
    activeIndexRef.current = nextActiveIndex;
    visualIndexRef.current = nextVisualIndex;
    isAnimatingRef.current = false;

    setActiveIndex(nextActiveIndex);
    setVisualIndex(nextVisualIndex);
    setDragOffset(0);
    setIsDragging(false);
    setIsAnimating(false);
    publishProgress(nextVisualIndex);

    const page = pageRefs.current[activeFilter];
    const pageScrollTop = page?.scrollTop || 0;

    onActivePageAtTopChange?.(pageScrollTop === 0);
  }, [
    activeFilter,
    clearSettleTimer,
    onActivePageAtTopChange,
    publishProgress,
  ]);

  useEffect(() => {
    return () => {
      clearSettleTimer();
      cancelProgressFrame();
    };
  }, [cancelProgressFrame, clearSettleTimer]);

  const handleTouchStart = useCallback((event) => {
    if (VISUAL_FILTERS.length <= 1 || isAnimatingRef.current) return;

    const touch = event.touches[0];
    if (!touch) return;

    gestureRef.current = {
      identifier: touch.identifier,
      startX: touch.clientX,
      startY: touch.clientY,
      currentX: touch.clientX,
      currentY: touch.clientY,
      startTime: performance.now(),
      axis: null,
    };

    setDragOffset(0);
    setIsDragging(false);
  }, []);

  const handleTouchMove = useCallback(
    (event) => {
      const gesture = gestureRef.current;
      if (!gesture) return;

      const touch = Array.from(event.touches).find(
        (item) => item.identifier === gesture.identifier
      );

      if (!touch) return;

      gesture.currentX = touch.clientX;
      gesture.currentY = touch.clientY;

      const dx = gesture.currentX - gesture.startX;
      const dy = gesture.currentY - gesture.startY;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      if (gesture.axis === null) {
        if (Math.max(absX, absY) < AXIS_LOCK_DISTANCE) {
          return;
        }

        if (absX > absY * HORIZONTAL_AXIS_RATIO) {
          gesture.axis = "horizontal";
          setIsDragging(true);
        } else {
          gestureRef.current = null;
          setIsDragging(false);
          setDragOffset(0);
          return;
        }
      }

      if (gesture.axis !== "horizontal") return;

      if (event.cancelable) {
        event.preventDefault();
      }

      const isDraggingBeforeFirst = visualIndexRef.current === 0 && dx > 0;
      const isDraggingAfterLast =
        visualIndexRef.current === VISUAL_FILTERS.length - 1 && dx < 0;

      const resistedDx =
        isDraggingBeforeFirst || isDraggingAfterLast
          ? dx * EDGE_DRAG_RESISTANCE
          : dx;

      const maxDrag = Math.max(viewportWidth, 1) * 1.02;
      const nextDragOffset = clamp(resistedDx, -maxDrag, maxDrag);

      setDragOffset(nextDragOffset);

      const width = Math.max(viewportWidth, 1);
      publishProgress(visualIndexRef.current - nextDragOffset / width);
    },
    [publishProgress, viewportWidth]
  );

  const settleGesture = useCallback(
    (clientX, clientY, cancelled = false) => {
      const gesture = gestureRef.current;
      gestureRef.current = null;

      if (!gesture || cancelled) {
        beginTrackTransition(visualIndexRef.current);
        return;
      }

      if (gesture.axis !== "horizontal") {
        setIsDragging(false);
        setDragOffset(0);
        return;
      }

      const dx = clientX - gesture.startX;
      const elapsed = Math.max(performance.now() - gesture.startTime, 1);
      const distance = Math.abs(dx);
      const velocity = distance / elapsed;
      const width = Math.max(viewportWidth, 1);

      const distanceThreshold = Math.max(
        SWIPE_MIN_DISTANCE,
        width * SWIPE_DISTANCE_RATIO
      );

      const shouldCommit =
        distance >= distanceThreshold ||
        (distance >= SWIPE_MIN_VELOCITY_DISTANCE &&
          velocity >= SWIPE_MIN_VELOCITY);

      if (distance >= AXIS_LOCK_DISTANCE) {
        suppressClickUntilRef.current = Date.now() + CLICK_SUPPRESSION_MS;
      }

      if (!shouldCommit) {
        beginTrackTransition(visualIndexRef.current);
        return;
      }

      const direction = dx < 0 ? 1 : -1;
      const targetVisualIndex = clamp(
        visualIndexRef.current + direction,
        0,
        VISUAL_FILTERS.length - 1
      );

      beginTrackTransition(targetVisualIndex);
    },
    [beginTrackTransition, viewportWidth]
  );

  const handleTouchEnd = useCallback(
    (event) => {
      const gesture = gestureRef.current;
      if (!gesture) return;

      const touch = Array.from(event.changedTouches).find(
        (item) => item.identifier === gesture.identifier
      );

      if (!touch) return;

      settleGesture(touch.clientX, touch.clientY, false);
    },
    [settleGesture]
  );

  const handleTouchCancel = useCallback(
    (event) => {
      const gesture = gestureRef.current;
      if (!gesture) return;

      const touch = Array.from(event.changedTouches).find(
        (item) => item.identifier === gesture.identifier
      );

      if (!touch) {
        gestureRef.current = null;
        beginTrackTransition(visualIndexRef.current);
        return;
      }

      settleGesture(touch.clientX, touch.clientY, true);
    },
    [beginTrackTransition, settleGesture]
  );

  const handleClickCapture = useCallback((event) => {
    if (Date.now() >= suppressClickUntilRef.current) return;

    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleTransitionEnd = useCallback(
    (event) => {
      if (event.target !== event.currentTarget) return;
      if (event.propertyName !== "transform") return;

      finishTrackTransition();
    },
    [finishTrackTransition]
  );

  useEffect(() => {
    if (!scrollToTopSignal) return;

    const activeFilterId = FILTERS[activeIndex];
    const activePage = pageRefs.current[activeFilterId];

    activePage?.scrollTo({
      top: 0,
      behavior: "auto",
    });

    onActivePageAtTopChange?.(true);
  }, [activeIndex, onActivePageAtTopChange, scrollToTopSignal]);

  const handlePageScroll = useCallback(
    (filter) => {
      if (filter !== FILTERS[activeIndex]) return;

      const page = pageRefs.current[filter];
      const pageScrollTop = page?.scrollTop || 0;

      onActivePageAtTopChange?.(!page || pageScrollTop === 0);
    },
    [activeIndex, onActivePageAtTopChange]
  );

  const trackTranslateX = -(visualIndex * viewportWidth) + dragOffset;

  return (
    <div className="most-visited-pager saved-pager saved-pager--android">
      <div
        className="most-visited-pager__container"
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        onClickCapture={handleClickCapture}
      >
        <div
          className={`saved-pager__track${
            isDragging ? " saved-pager__track--dragging" : ""
          }${isAnimating ? " saved-pager__track--animating" : ""}`}
          style={{
            transform: `translate3d(${trackTranslateX}px, 0, 0)`,
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {VISUAL_FILTERS.map((filter, pageVisualIndex) => {
            const isActivePage = pageVisualIndex === visualIndex;

            return (
              <SavedPagerPage
                key={`${filter}-${pageVisualIndex}`}
                ref={(node) => {
                  if (node) {
                    pageRefs.current[filter] = node;
                  } else {
                    delete pageRefs.current[filter];
                  }
                }}
                filter={filter}
                pageItems={pages[filter] || []}
                className={
                  isActivePage ? "" : "saved-pager__page--inactive"
                }
                onScroll={() => handlePageScroll(filter)}
                favicons={favicons}
                onMarkAsRead={onMarkAsRead}
                onNewsOpen={onNewsOpen}
                savedLinks={savedLinks}
                onToggleSaved={onToggleSaved}
                onShare={onShare}
                onView={onView}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SavedPagerAndroid;
